import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Avatar, AvatarImage, AvatarFallback } from "../components/ui/avatar";
import { ArrowLeft, Camera, Save } from "lucide-react";
import { toast } from "sonner";

const ProfileSettings = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [picturePreview, setPicturePreview] = useState(user?.picture || "");
  const [saving, setSaving] = useState(false);

  const handlePictureChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Imagem demasiado grande (máx 2MB)");
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setPicturePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Update name and phone
      await axios.patch(`${API}/auth/profile`, { name, phone }, { withCredentials: true });

      // Upload picture if changed
      if (picturePreview && picturePreview !== user?.picture && picturePreview.startsWith("data:")) {
        await axios.post(`${API}/auth/profile/picture`, { picture: picturePreview }, { withCredentials: true });
      }

      toast.success("Perfil atualizado com sucesso!");
      if (refreshUser) await refreshUser();
    } catch {
      toast.error("Erro ao guardar perfil");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f0f4f5] to-[#e8eff1]">
      <div className="max-w-xl mx-auto px-4 py-8">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6 text-[#6697a4]" data-testid="back-btn">
          <ArrowLeft className="mr-2 h-4 w-4" /> Voltar
        </Button>

        <Card className="bg-white border-0 shadow-sm rounded-2xl">
          <CardHeader>
            <CardTitle className="text-[#3d5a63]">Definições de Perfil</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Picture */}
            <div className="flex flex-col items-center gap-4">
              <div className="relative cursor-pointer" onClick={() => fileInputRef.current?.click()} data-testid="picture-upload">
                <Avatar className="h-28 w-28 ring-4 ring-[#6697a4]/20">
                  <AvatarImage src={picturePreview} />
                  <AvatarFallback className="bg-[#6697a4] text-white text-3xl">{name?.charAt(0)}</AvatarFallback>
                </Avatar>
                <div className="absolute bottom-0 right-0 bg-[#6697a4] text-white rounded-full p-2 shadow-lg">
                  <Camera className="h-4 w-4" />
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePictureChange}
                className="hidden"
                data-testid="picture-input"
              />
              <p className="text-sm text-[#94A3B8]">Clica para alterar a foto (máx 2MB)</p>
            </div>

            {/* Name */}
            <div>
              <Label htmlFor="name">Nome</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name" />
            </div>

            {/* Phone */}
            <div>
              <Label htmlFor="phone">Número de Telefone (internacional)</Label>
              <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+351 912 345 678" data-testid="profile-phone" />
              <p className="text-xs text-[#94A3B8] mt-1">Inclui o código do país (ex: +351)</p>
            </div>

            {/* Email (read-only) */}
            <div>
              <Label>Email</Label>
              <Input value={user?.email || ""} disabled className="bg-slate-50" />
            </div>

            <Button onClick={handleSave} disabled={saving} className="w-full bg-[#6697a4] hover:bg-[#3d5a63]" data-testid="save-profile-btn">
              <Save className="mr-2 h-4 w-4" /> {saving ? "A guardar..." : "Guardar Alterações"}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProfileSettings;
